<i18n src="./resources/locales.json"></i18n>

<template>
  <div class="ecs-agent-state white--text" :class="blinkStyle" @click="menuOpen = !menuOpen">
    <label v-once>{{ $t('label') }}</label>
    <span v-if="isRunningInsideTeams" class="ms-teams-state-sync">
      <v-tooltip bottom open-on-hover>
        <template v-slot:activator="act">
          <v-icon small class="ms-teams-state-icon" v-on="act.on">mdi-microsoft-teams</v-icon>
        </template>
        <span>{{ $t('msTeamsStateSync') }}</span>
      </v-tooltip>
    </span>
    <v-progress-circular
      v-show="waitingStateChange"
      class="state-update"
      width="2"
      color="white"
      indeterminate
      size="16"
    />
    <!--label class="state-time">{{ timeInState }}</label-->
    <v-menu
      id="agent-state-menu"
      ref="agentStateMenu"
      v-model="menuOpen"
      :disabled="isOfflineModeEnabled"
      content-class="agent-state-menu"
      bottom
      offset-y
      z-index="160"
    >
      <template v-slot:activator="{ on }">
        <v-row id="activator" class="agent-state-dropdown" align="center" no-gutters v-on="on" @click="checkStateList">
          <v-icon small color="white">{{ agentStateIcon }}</v-icon>
          <span id="txt_current_state" ref="text">
            {{ agentStateName }}
          </span>
          <v-spacer />
          <v-icon id="icon-dropdown" ref="dropdownIcon" dark :class="{ 'mdi-rotate-180': menuOpen }">
            mdi-menu-down
          </v-icon>
        </v-row>
      </template>
      <v-list v-if="availableAgentStates.length">
        <v-list-item v-for="state in availableAgentStates" :key="state.id" @click="changeAgentState(state)">
          <v-list-item-icon v-if="getIcon(state)" class="list-icon">
            <v-icon small>{{ getIcon(state) }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content v-if="state.name !== 'Break'" class="state-change-option">
            {{ state.name }}
          </v-list-item-content>

          <!-- Sub-menu for Break Types -->
          <v-menu v-else class="px-0" offset-x open-on-hover>
            <template v-slot:activator="{ on }">
              <v-list-item-content v-on="on">
                <v-row align="center" justify="space-between" no-gutters>
                  {{ state.name }}
                  <v-spacer></v-spacer>
                  <v-icon v-if="breakTypes.length">mdi-menu-right</v-icon>
                </v-row>
              </v-list-item-content>
            </template>
            <v-list v-if="breakTypes.length" dense class="breaks-menu">
              <v-list-item
                v-for="breakType in breakTypes"
                :key="breakType.id"
                @click="changeAgentBreakState(breakType)"
              >
                <v-list-item-icon v-if="getIcon(breakType)" class="list-icon mx-1">
                  <v-icon small>{{ getIcon(breakType) }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ breakType.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-list-item>
      </v-list>

      <v-list v-else class="text-center">
        <v-progress-circular :width="2" :size="20" color="white" indeterminate />
        <span v-once>{{ $t('states.loading') }}</span>
      </v-list>
    </v-menu>
    <v-progress-linear v-if="showProgress" class="progress" :value="progressLinearValue" color="accent" />

    <EcsConfirmationDialog
      ref="confirmationDialog"
      :title="$t('dialog.title')"
      :message="$t('dialog.message')"
      :checkbox-text="$t('dialog.checkboxText')"
      :confirm-button-text="$t('dialog.confirm')"
      :cancel-button-text="$t('dialog.cancel')"
      @confirm-checkbox="saveOfflineWarningPreference"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { app } from '@microsoft/teams-js';
import EcsConfirmationDialog from '@/components/ecs-confirmation-dialog/EcsConfirmationDialog';
import EcsState from '@/components/ecs-agent-state/EcsState';
import AgentService from '@/services/agentService';
import { AGENT_STATE } from '@/constants/agent/constants';
import EcsKpiFormatter from '@/mixins/ecs-kpi-formatter/EcsKpiFormatter';
import EcsSoftPhoneMixin from '@/components/ecs-soft-phone/EcsSoftPhoneMixin';
import { agentSubscriptions } from '@/constants/kpi-counters/subscriptions';

const SNACKBAR_MESSAGE_EVENT = 'snackbar-message';

export default {
  name: 'EcsAgentState',

  components: { EcsConfirmationDialog },

  mixins: [EcsKpiFormatter, EcsSoftPhoneMixin],

  data() {
    return {
      timeInState: '',
      stateTimeCycleRef: null,
      menuOpen: false,
      breakStateTimer: null,
      breakStateAlert: null,
      remainingBreakTime: 0,
      isRunningInsideTeams: false
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent', 'agentState']),

    ...mapGetters('REFERENCES', ['availableAgentStates', 'availableCustomBreakStates']),

    ...mapGetters('SETTINGS', ['isOfflineModeEnabled', 'preferencesSettings']),

    ...mapGetters('EVENT_TIMING', ['isWaitingForAgentState']),

    ...mapGetters('INTERACTIONS', ['getActiveInteractions']),

    ...mapGetters('ORGANIZATION', ['breakTypes']),

    ...mapGetters('STATS', ['loggedInAgentStats']),

    waitingStateChange() {
      return this.isWaitingForAgentState(this.loggedInAgent.id);
    },

    agentStateIcon() {
      let icon = null;
      const id = this.agentState.stateId;
      const isBreak = this.agentState.isBreak;
      if (isBreak) return 'mdi-coffee';
      switch (id) {
        case AGENT_STATE.backoffice:
          icon = 'mdi-paperclip';
          break;
        case AGENT_STATE.offline:
          icon = 'mdi-logout';
          break;
        case AGENT_STATE.ready:
          icon = 'mdi-power';
          break;
        case AGENT_STATE.break:
          icon = 'mdi-coffee';
          break;
        case AGENT_STATE.outgoing:
          icon = 'mdi-phone-outgoing';
          break;
        case AGENT_STATE.busy:
        case AGENT_STATE.dialing:
          icon = 'mdi-minus-circle';
          break;
        case AGENT_STATE.offered:
          icon = 'mdi-tag';
          break;
        case AGENT_STATE.preview:
          icon = 'mdi-motion-play';
          break;
        case AGENT_STATE.waitingForOffline:
        case AGENT_STATE.waitingForBackoffice:
        case AGENT_STATE.waitingForBreak:
        case AGENT_STATE.waitingForOutgoing:
          icon = 'mdi-clock';
          break;
        default:
          break;
      }
      return icon;
    },

    agentStateName() {
      const stateName =
        this.agentState.isBreak && !this.agentState.isNoAnswer
          ? `${this.agentState.reasonCode || this.agentState.stateName}`
          : this.agentState.stateName;

      return stateName.replaceAll(/([a-z])([A-Z])/g, '$1 $2');
    },

    agentStateStyle() {
      let stateClass = 'offline';
      if (this.agentState.isReady) {
        stateClass = 'ready';
      } else if (this.agentState.isBreak) {
        stateClass = 'break';
      } else if (this.agentState.isBusy || this.agentState.isOutgoing || this.agentState.isBackoffice) {
        stateClass = 'busy';
      }
      return stateClass;
    },

    blinkStyle() {
      let result = {};

      if (this.isBlinking) {
        result = {
          blink: this.$vuetify.theme.dark,
          'light-blink': !this.$vuetify.theme.dark
        };
      }

      return result;
    },

    breakDuration() {
      if (!this.agentState.isBreak) {
        return 0;
      }

      let result = this.agentState.breakDuration;

      if (!result) {
        const breakState = this.breakTypes.find(state => state.id === this.agentState.stateId);
        result = breakState ? breakState.expirationMinutes : 0;
      }

      return result * 60;
    },

    isBlinking() {
      return this.remainingBreakTime > 0 && this.preferencesSettings.alertEndOfBreak > this.remainingBreakTime;
    },

    progressLinearValue() {
      return (100 * this.remainingBreakTime) / this.breakDuration;
    },

    showProgress() {
      return this.remainingBreakTime > 0;
    }
  },

  watch: {
    breakDuration(newValue) {
      if (this.breakStateTimer) {
        clearInterval(this.breakStateTimer);
      }
      if (this.breakStateAlert) {
        clearTimeout(this.breakStateAlert);
      }
      this.remainingBreakTime = 0;

      if (newValue) {
        const stateChangeTimestamp =
          this.agentState.stateChangeTimestamp || sessionStorage.getItem('breakStateChangeTimestamp') || moment();

        this.remainingBreakTime = this.getRemainingBreakStateTime(stateChangeTimestamp, newValue);

        this.setupBreakStateAlert(this.remainingBreakTime);
        this.setupBreakStateTimer();

        // Patch to keep the break state change timestamp in local storage if refresh
        sessionStorage.setItem('breakStateChangeTimestamp', stateChangeTimestamp);
      } else {
        sessionStorage.removeItem('breakStateChangeTimestamp');
      }
    }
  },

  created() {
    this.getAgentStateList();
    this.getBreakTypeList();
    this.stateTimeCycle();
    this.msTeamsContext();

    this.$root.$on('submit-agent-state-change', this.submitAgentStateChange);
  },

  beforeDestroy() {
    clearInterval(this.breakStateTimer);
    clearInterval(this.stateTimeCycleRef);

    this.$root.$off('submit-agent-state-change', this.submitAgentStateChange);
  },

  methods: {
    ...mapActions('REFERENCES', ['requestAvailableAgentStates', 'requestAvailableAgentCustomBreakStates']),

    ...mapActions('EVENT_TIMING', ['addEventTimingConstraintForAgentState', 'addEventTimingConstraintForKPICounter']),

    async msTeamsContext() {
      try {
        await app.initialize();
        await app.getContext();
        this.isRunningInsideTeams = true;
      } catch (error) {
        this.isRunningInsideTeams = false;
      }
    },

    stateTimeCycle() {
      this.stateTimeCycleRef = setInterval(() => {
        if (this.loggedInAgentStats && this.loggedInAgentStats.agentTimeInState) {
          this.timeInState = this.kpiFormat(this.loggedInAgentStats.agentTimeInState.value, 'duration', true);
        }
      }, 1000);
    },

    emitError() {
      this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
        message: this.$t('errors.update_agent_state'),
        type: 'error'
      });
    },

    changeAgentBreakState(breakType) {
      this.menuOpen = false;
      this.registerSoftPhone().then(() => {
        AgentService.updateAgentCustomBreakState(breakType.id).catch(() => {
          this.emitError();
        });
      });
    },

    async changeAgentState(state) {
      let confirmed;

      if (EcsState.readyStateIds.includes(state.id) && EcsState.breakStateIds.includes(this.agentState.stateId)) {
        const internalInteractions = this.getActiveInteractions.filter(interaction => interaction.isInternal);
        if (internalInteractions.length) {
          return this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
            message: this.$t('privateInteractionShouldEndBefore'),
            type: 'info',
            showClose: true
          });
        }
      }

      if (EcsState.offlineStateIds.includes(state.id)) {
        // User must confirm going Offline
        confirmed = await this.requestUserConfirmation();
        if (confirmed) {
          await this.submitAgentStateChange(state);
        }
      } else {
        await this.submitAgentStateChange(state);
      }

      return this.getActiveInteractions.length ? false : confirmed;
    },

    checkStateList() {
      if (this.availableAgentStates.length === 0) {
        this.getAgentStateList();
      }
    },

    getAgentStateList() {
      this.requestAvailableAgentStates().catch(error => {
        console.info('Could not get agent state list from server', error);
      });
    },

    getBreakTypeList() {
      this.requestAvailableAgentCustomBreakStates().catch(error => {
        console.info('Could not get break type list from server', error);
      });
    },

    /**
     * Gets the appropriate icon name for a known Agent State for aesthetics
     * @returns {string}
     */
    getIcon(state) {
      let icon = null;
      switch (state.id) {
        case AGENT_STATE.backoffice:
          icon = 'mdi-paperclip';
          break;
        case AGENT_STATE.offline:
          icon = 'mdi-logout';
          break;
        case AGENT_STATE.ready:
          icon = 'mdi-power';
          break;
        case AGENT_STATE.break:
          icon = 'mdi-coffee';
          break;
        case AGENT_STATE.outgoing:
          icon = 'mdi-phone-outgoing';
          break;
        case AGENT_STATE.busy:
          icon = 'mdi-minus-circle';
          break;
        case AGENT_STATE.offered:
          icon = 'mdi-tag';
          break;
        case AGENT_STATE.waitingForOffline:
          icon = 'mdi-clock';
          break;
        case AGENT_STATE.waitingForBackoffice:
          icon = 'mdi-clock';
          break;
        case AGENT_STATE.waitingForBreak:
          icon = 'mdi-clock';
          break;
        case AGENT_STATE.waitingForOutgoing:
          icon = 'mdi-clock';
          break;
        default:
          break;
      }
      return icon;
    },

    /**
     * Requests user confirmation
     * Note: This will always return true if the user has selected 'Don't ask me again'
     * with a previous confirmation
     * @returns {Promise<boolean>}
     */
    requestUserConfirmation() {
      const isOfflineWarningDisabled = localStorage.getItem('offlineWarningDisabled') === this.loggedInAgent.id;
      return isOfflineWarningDisabled ? Promise.resolve(true) : this.$refs.confirmationDialog.open();
    },

    /**
     * Saves the 'Don't ask me again' preference to localStorage
     * @param {boolean} checked
     */
    saveOfflineWarningPreference(checked) {
      localStorage.setItem('offlineWarningDisabled', checked ? this.loggedInAgent.id : null);
    },

    /**
     * Sends an Agent State change request to the API
     * @param {object} state
     * @param {function} state.onSuccess - Optional callback function to execute on success
     * @param {function} state.onError - Optional callback function to execute on error
     */
    submitAgentStateChange(state) {
      if (state.id !== this.agentState.stateId) {
        const directCallInteractions = this.getActiveInteractions.filter(interaction => interaction.isDirectCall);
        if (!EcsState.busyStateIds.includes(this.agentState.stateId) && directCallInteractions.length === 0) {
          this.addEventTimingConstraintForAgentState({ agentId: this.loggedInAgent.id });
        }
        this.registerSoftPhone(EcsState.offlineStateIds.includes(state.id)).then(() => {
          const newStateId = state.id;
          const oldStateId = this.agentState.stateId;
          if (!EcsState.busyStateIds.includes(newStateId)) {
            this.addEventTimingConstraintForKPICounters({ stateId: newStateId, agentId: this.loggedInAgent.id });
            this.addEventTimingConstraintForKPICounters({ stateId: oldStateId, agentId: this.loggedInAgent.id });
          }
          return AgentService.updateLoggedInAgentState(state.id)
            .then(() => state.onSuccess && state.onSuccess())
            .catch(() => {
              state.onError && state.onError();
              this.emitError();
            });
        });
      } else if (state.onSuccess) {
        state.onSuccess();
      }
    },

    addEventTimingConstraintForKPICounters({ stateId, agentId }) {
      if (EcsState.offlineStateIds.includes(stateId)) {
        this.addEventTimingConstraintForKPICounter({
          counterTypeId: agentSubscriptions.loginTime.counterTypeId,
          handlerId: agentId
        });
      } else if (EcsState.readyStateIds.includes(stateId)) {
        this.addEventTimingConstraintForKPICounter({
          counterTypeId: agentSubscriptions.readyTime.counterTypeId,
          handlerId: agentId
        });
      } else if (EcsState.busyStateIds.includes(stateId)) {
        this.addEventTimingConstraintForKPICounter({
          counterTypeId: agentSubscriptions.busyTime.counterTypeId,
          handlerId: agentId
        });
      } else if (EcsState.breakStateIds.includes(stateId)) {
        this.addEventTimingConstraintForKPICounter({
          counterTypeId: agentSubscriptions.breakTime.counterTypeId,
          handlerId: agentId
        });
      } else if (EcsState.backofficeStateIds.includes(stateId)) {
        this.addEventTimingConstraintForKPICounter({
          counterTypeId: agentSubscriptions.backofficeTime.counterTypeId,
          handlerId: agentId
        });
      } else if (EcsState.outgoingStateIds.includes(stateId)) {
        this.addEventTimingConstraintForKPICounter({
          counterTypeId: agentSubscriptions.outgoingTime.counterTypeId,
          handlerId: agentId
        });
      }
    },

    getRemainingBreakStateTime(timestamp, breakDuration) {
      const breakEndTime = moment(timestamp).add(breakDuration, 'seconds');
      const result = Math.ceil(breakEndTime.diff(moment(), 'seconds', true));
      return Math.max(result, 0);
    },

    setupBreakStateAlert(remainingBreakTime) {
      if (!this.preferencesSettings.alertEndOfBreak) {
        return;
      }

      const timeout = Math.max(remainingBreakTime - this.preferencesSettings.alertEndOfBreak, 0);
      const time = timeout > 0 ? this.preferencesSettings.alertEndOfBreak : remainingBreakTime;

      this.breakStateAlert = setTimeout(() => {
        const message = time < 60 ? this.$t('seconds', [time]) : this.$tc('minutes', Math.trunc(time / 60));
        this.$root.$emit(SNACKBAR_MESSAGE_EVENT, {
          message: this.$t('alertEndOfBreak', { time: message }),
          type: 'info',
          timeout: time * 1000,
          showClose: true
        });
      }, timeout * 1000);
    },

    setupBreakStateTimer() {
      this.breakStateTimer = setInterval(() => {
        if (this.remainingBreakTime > 0) {
          this.remainingBreakTime--;
        } else {
          clearInterval(this.breakStateTimer);
          sessionStorage.removeItem('breakStateChangeTimestamp');
        }
      }, 1000);
    }
  }
};
</script>

<style src="./scoped.css" scoped />
<style>
.ecs-agent-state #activator {
  margin-top: -3px;
}

.ecs-agent-state .v-icon.v-icon:not(.ms-teams-state-icon) {
  transition: color 0.2s ease-in-out;
  color: transparent;
}

.ecs-agent-state:hover .v-icon.v-icon {
  color: #fffa;
}

.agent-state-menu .list-icon:first-child {
  margin-right: 8px;
}

.v-dialog.v-dialog--active,
.confirm-dialog.smaller-radius,
.confirm-dialog .smaller-radius {
  border-radius: 4px;
}

.confirm-dialog .headline {
  border-radius: 4px 4px 0 0;
}

.confirm-dialog .headline .warning-icon {
  font-size: 24px !important;
  margin-right: 8px;
}

.agent-state-dropdown {
  cursor: pointer;
  position: relative;
}

.state-update {
  margin: -4px 0 0 12px;
}

.breaks-menu {
  max-height: 200px;
  overflow-y: scroll;
}

.light-blink {
  -webkit-transition: all 0.2s ease !important;
  -moz-transition: all 0.2s ease !important;
  -ms-transition: all 0.2s ease !important;
  -o-transition: all 0.2s ease !important;
  transition: all 0.2s ease !important;

  background-color: var(--v-primary-base);
  animation: light-theme-blink 2s linear infinite alternate both !important;
  -webkit-animation: light-theme-blink 2s linear infinite alternate both !important;
}

/* Blink animation */
@keyframes light-theme-blink {
  0% {
    background-color: var(--v-primary-base);
  }
  50% {
    background-color: var(--v-primary-lighten1);
  }
  100% {
    background-color: var(--v-primary-base);
  }
}
</style>
